import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadHandler } from './use-upload-handler';

/**
 * File picker that hands the selected file to the query worker for
 * in-worker parsing. Supports `.csv`, `.xlsx`, `.xls`. All ingest
 * lifecycle (worker call, profile, dashboardName rehydration, error
 * reporting, storage refresh) lives in {@link useUploadHandler}.
 */
export function UploadButton(): JSX.Element {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { busy, blocked, handleFile } = useUploadHandler();

  async function onChange(
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    const file = e.target.files?.item(0) ?? null;
    await handleFile(file);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy || blocked}
        title={blocked ? t('upload.blockedTooltip') : undefined}
        className="w-full flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-md text-white text-xs font-semibold disabled:opacity-50"
        style={{ background: 'var(--color-accent)' }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path
            d="M8 3v10M3 8h10"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        {busy ? t('upload.loading') : t('upload.button')}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={(e) => {
          void onChange(e);
        }}
        className="hidden"
        aria-label={t('upload.button')}
      />
    </div>
  );
}
